import * as duckdb from '@duckdb/duckdb-wasm';

// Singleton — DuckDB boots once per browser session
let dbInstance = null;
let loadingPromise = null;
export let duckDbLoading = false;

function makeWorkerBlob(workerUrl) {
  // Blob wrapper sidesteps cross-origin Worker restriction
  return URL.createObjectURL(
    new Blob([`importScripts('${workerUrl}');`], { type: 'application/javascript' })
  );
}

async function bootDuckDb() {
  const bundles = duckdb.getJsDelivrBundles();
  const bundle = await duckdb.selectBundle(bundles);

  const workerBlobUrl = makeWorkerBlob(bundle.mainWorker);
  const worker = new Worker(workerBlobUrl);
  const logger = new duckdb.VoidLogger();
  const db = new duckdb.AsyncDuckDB(logger, worker);
  await db.instantiate(bundle.mainModule);
  URL.revokeObjectURL(workerBlobUrl);
  return db;
}

export async function getDuckDb() {
  if (dbInstance) return dbInstance;
  if (!loadingPromise) {
    duckDbLoading = true;
    loadingPromise = bootDuckDb()
      .then((db) => {
        dbInstance = db;
        duckDbLoading = false;
        return db;
      })
      .catch((e) => {
        loadingPromise = null;
        duckDbLoading = false;
        throw e;
      });
  }
  return loadingPromise;
}

// Convert Apache Arrow Table → { rows: string[][], columns: string[], output: string }
function arrowToResult(table) {
  const columns = table.schema.fields.map((f) => f.name);

  if (table.numRows === 0) {
    const colList = columns.length ? `Columns: ${columns.join(', ')}` : '';
    return { rows: [], columns, output: `(no rows returned)\n${colList}`.trim() };
  }

  const rows = [];
  for (const batch of table.batches) {
    for (let ri = 0; ri < batch.numRows; ri++) {
      const row = columns.map((_, ci) => {
        const v = batch.getChildAt(ci)?.get(ri);
        if (v === null || v === undefined) return 'NULL';
        if (typeof v === 'bigint') return String(v);
        if (v instanceof Date) return v.toISOString().slice(0, 19).replace('T', ' ');
        return String(v);
      });
      rows.push(row);
    }
  }

  // Render as aligned text table
  const colWidths = columns.map((c, i) =>
    Math.max(c.length, ...rows.map((r) => r[i].length))
  );
  const sep = colWidths.map((w) => '─'.repeat(w + 2)).join('┼');
  const header = columns.map((c, i) => ` ${c.padEnd(colWidths[i])} `).join('│');
  const rowLines = rows.map((row) =>
    row.map((cell, i) => ` ${cell.padEnd(colWidths[i])} `).join('│')
  );
  const output = [header, sep, ...rowLines, `\n${rows.length} row(s)  ·  DuckDB`].join('\n');

  return { rows, columns, output };
}

/**
 * Run one or more SQL statements in an isolated DuckDB connection.
 *
 * @param {string} sql        - User SQL (semicolons separate statements)
 * @param {string} csvData    - Optional CSV string pre-loaded as `lesson_data` table
 * @param {string} setupSql   - Optional setup DDL/DML to run before user SQL
 */
export async function runDuckDb(sql, csvData = '', setupSql = '') {
  const db = await getDuckDb();
  const conn = await db.connect();

  try {
    // Register CSV from lesson context
    if (csvData.trim()) {
      await db.registerFileText('lesson_data.csv', csvData);
      await conn.query(
        `CREATE OR REPLACE TABLE lesson_data AS
         SELECT * FROM read_csv_auto('lesson_data.csv')`
      );
    }

    // Run any lesson-provided setup (CREATE TABLE, INSERT …)
    if (setupSql.trim()) {
      for (const stmt of setupSql.split(';').map((s) => s.trim()).filter(Boolean)) {
        await conn.query(stmt);
      }
    }

    // Run user statements; return the last result that has schema
    const stmts = sql.split(';').map((s) => s.trim()).filter(Boolean);
    let lastTable = null;
    for (const stmt of stmts) {
      lastTable = await conn.query(stmt);
    }

    if (!lastTable) {
      return { rows: [], columns: [], output: '(no result)', error: '' };
    }

    const { rows, columns, output } = arrowToResult(lastTable);
    return { rows, columns, output, error: '' };
  } catch (e) {
    return { rows: [], columns: [], output: '', error: e.message };
  } finally {
    await conn.close();
  }
}
